<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import BarChart from 'shared/components/charts/BarChart.vue';
import { bucketLabel } from '../../helper/crmBuckets';
import ReportMetricCard from '../ReportMetricCard.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import CrmDealsReportsAPI from 'dashboard/api/reports/crmDeals';

const { t } = useI18n();

const DATE_FILTERS = {
  TODAY: 'today',
  LAST_WEEK: 'last_week',
  LAST_MONTH: 'last_month',
  CUSTOM: 'custom'
};

const selectedDateFilter = ref(null);
const customDateRange = ref([new Date(), new Date()]);
const isLoading = ref(false);
// Agregados calculados en el backend (V2::Reports::Crm::DealsBuilder), acotados al rango.
const summary = ref(null);

const dateFilterOptions = computed(() => [
  { id: DATE_FILTERS.TODAY, name: t('CRM_REPORTS.LEADS.FILTERS.TODAY') },
  { id: DATE_FILTERS.LAST_WEEK, name: t('CRM_REPORTS.LEADS.FILTERS.LAST_WEEK') },
  { id: DATE_FILTERS.LAST_MONTH, name: t('CRM_REPORTS.LEADS.FILTERS.LAST_MONTH') },
  { id: DATE_FILTERS.CUSTOM, name: t('CRM_REPORTS.LEADS.FILTERS.CUSTOM') }
]);

onMounted(() => {
  selectedDateFilter.value = dateFilterOptions.value[0];
});

const dateRange = computed(() => {
  if (!selectedDateFilter.value) return { start: new Date(), end: new Date() };
  
  const today = new Date();
  
  switch (selectedDateFilter.value.id) {
    case DATE_FILTERS.TODAY:
      return {
        start: startOfDay(today),
        end: endOfDay(today)
      };
    case DATE_FILTERS.LAST_WEEK:
      return {
        start: startOfDay(subDays(today, 7)),
        end: endOfDay(today)
      };
    case DATE_FILTERS.LAST_MONTH:
      return {
        start: startOfDay(subDays(today, 30)),
        end: endOfDay(today)
      };
    case DATE_FILTERS.CUSTOM:
      return {
        start: startOfDay(customDateRange.value[0]),
        end: endOfDay(customDateRange.value[1])
      };
    default:
      return {
        start: startOfDay(today),
        end: endOfDay(today)
      };
  }
});

const fetchSummary = async () => {
  const { start, end } = dateRange.value;
  isLoading.value = true;
  try {
    const { data } = await CrmDealsReportsAPI.getSummary({
      since: Math.floor(start.getTime() / 1000),
      until: Math.floor(end.getTime() / 1000),
      sections: ['created', 'won', 'close_rate']
    });
    summary.value = data;
  } catch (error) {
    summary.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(dateRange, fetchSummary);

// Granularidad elegida por el backend según el largo del rango (día / semana / mes).
const granularity = computed(() => summary.value?.range?.granularity || 'day');
const labelFor = timestamp => bucketLabel(timestamp, granularity.value);

const dealsCount = computed(() => summary.value?.created?.count ?? 0);

const totalAmounts = computed(
  () => summary.value?.created?.amounts || { ars: 0, usd: 0 }
);

// Helper para formatear números con separadores de miles
const formatCurrency = (value, currency) => {
  const formatted = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

  return currency === 'ars' ? `$${formatted}` : `US$${formatted}`;
};

const formattedTotalARS = computed(() => formatCurrency(totalAmounts.value.ars, 'ars'));
const formattedTotalUSD = computed(() => formatCurrency(totalAmounts.value.usd, 'usd'));

// ===== NEGOCIOS GANADOS =====
// Ganado = status `won`. Antes se usaba `closed_at`, que también dejaba pasar perdidos
// y cancelados que nunca llegaron al stage ganado.
const closedDealsCount = computed(() => summary.value?.won?.count ?? 0);

// Tasa de cierre: ganados en el rango sobre creados en el rango.
const closeRate = computed(() => Math.round(summary.value?.close_rate ?? 0));

const makeChartData = (points, label) => ({
  labels: points.map(p => labelFor(p.timestamp)),
  datasets: [
    {
      label,
      data: points.map(p => p.value),
      backgroundColor: 'rgb(59, 130, 246)',
      borderColor: 'rgb(37, 99, 235)',
      borderWidth: 1
    }
  ]
});

const dealsClosedChartData = computed(() =>
  makeChartData(summary.value?.won?.series || [], t('CRM_REPORTS.DEALS.CHART.DEALS_CLOSED'))
);

const dealsCreatedChartData = computed(() =>
  makeChartData(summary.value?.created?.series || [], t('CRM_REPORTS.DEALS.CHART.DEALS_CREATED'))
);

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      enabled: true
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1
      }
    }
  }
};

const onDateRangeChange = (value) => {
  customDateRange.value = value;
};

watch(selectedDateFilter, (newValue) => {
  if (newValue && newValue.id !== DATE_FILTERS.CUSTOM) {
    customDateRange.value = [new Date(), new Date()];
  }
}, { deep: true });
</script>

<template>
  <div class="space-y-6">
    <div class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('CRM_REPORTS.DEALS.TITLE') }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {{ $t('CRM_REPORTS.DEALS.DESCRIPTION') }}
          </p>
        </div>
        
        <div class="flex items-center gap-4">
          <div class="multiselect-wrap--small w-48">
            <multiselect
              v-model="selectedDateFilter"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')"
              :options="dateFilterOptions"
              :searchable="false"
              :allow-empty="false"
              :show-labels="false"
            />
          </div>
          
          <WootDateRangePicker
            v-if="selectedDateFilter && selectedDateFilter.id === DATE_FILTERS.CUSTOM"
            :value="customDateRange"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            @change="onDateRangeChange"
          />
        </div>
      </div>
      
      <!-- Métricas -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <ReportMetricCard
          :label="$t('CRM_REPORTS.DEALS.METRICS.CREATED.LABEL')"
          :value="dealsCount.toString()"
          :info-text="$t('CRM_REPORTS.DEALS.METRICS.CREATED.INFO')"
        />
        
        <!-- Valor Aproximado -->
        <div class="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
          <div class="flex items-start justify-between mb-2">
            <div class="flex items-center gap-2">
              <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ $t('CRM_REPORTS.DEALS.METRICS.TOTAL_VALUE.LABEL') }}
              </h4>
              <button
                class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                :title="$t('CRM_REPORTS.DEALS.METRICS.TOTAL_VALUE.INFO')"
              >
                <fluent-icon icon="info" size="16" />
              </button>
            </div>
            <div class="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="money" size="20" class="text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          
          <div class="space-y-2 mt-3">
            <!-- Pesos Argentinos -->
            <div class="flex items-baseline justify-between">
              <span class="text-xs text-slate-600 dark:text-slate-400 font-medium">ARS</span>
              <span class="text-lg font-bold text-slate-900 dark:text-slate-100">
                {{ formattedTotalARS }}
              </span>
            </div>
            
            <!-- Dólares -->
            <div class="flex items-baseline justify-between">
              <span class="text-xs text-slate-600 dark:text-slate-400 font-medium">USD</span>
              <span class="text-lg font-bold text-slate-900 dark:text-slate-100">
                {{ formattedTotalUSD }}
              </span>
            </div>
          </div>
          
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-3">
            {{ $t('CRM_REPORTS.DEALS.METRICS.TOTAL_VALUE.SUBTITLE') }}
          </p>
        </div>
        
        <!-- Deals Cerrados (Ganados) -->
        <div class="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
          <div class="flex items-start justify-between mb-2">
            <div class="flex items-center gap-2">
              <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ $t('CRM_REPORTS.DEALS.METRICS.CLOSED.LABEL') }}
              </h4>
              <button
                class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                :title="$t('CRM_REPORTS.DEALS.METRICS.CLOSED.INFO')"
              >
                <fluent-icon icon="info" size="16" />
              </button>
            </div>
            <div class="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="checkmark-circle" size="20" class="text-green-600 dark:text-green-400" />
            </div>
          </div>
          
          <div class="flex items-baseline justify-between mt-3">
            <span class="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {{ closedDealsCount }}
            </span>
            <div class="flex items-center gap-1">
              <span class="text-lg font-semibold text-green-600 dark:text-green-400">
                {{ closeRate }}%
              </span>
              <span class="text-xs text-slate-500 dark:text-slate-400">
                tasa de cierre
              </span>
            </div>
          </div>
          
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-3">
            {{ $t('CRM_REPORTS.DEALS.METRICS.CLOSED.SUBTITLE') }}
          </p>
        </div>
      </div>
      
      <!-- Gráfico de Deals Creados -->
      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          {{ $t('CRM_REPORTS.DEALS.CHART.TITLE') }}
        </h4>
        <div class="h-80">
          <BarChart v-if="dealsCount > 0" :collection="dealsCreatedChartData" :chart-options="chartOptions" />
          <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
            No hay datos de deals para el período seleccionado
          </div>
        </div>
      </div>
      
      <!-- Gráfico de Deals Cerrados -->
      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          {{ $t('CRM_REPORTS.DEALS.CHART.CLOSED_TITLE') }}
        </h4>
        <div class="h-80">
          <BarChart v-if="closedDealsCount > 0" :collection="dealsClosedChartData" :chart-options="chartOptions" />
          <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
            No hay datos de deals para el período seleccionado
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
